// Éléments communs des fiches courrier et dossier : historique (chemin des états), pièces.
import type { DocumentResume, EvenementHistorique } from '@cc/contrats/ui-metier';
import { Bouton, EtatVide, FichierChamp, Lien, Squelette } from '@cc/ui';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { requete } from '../../api/client';
import { messageErreur } from '../../api/erreurs';
import { AffichageErreur } from '../../commun/EtatRequete';
import { dateHeure } from '../../commun/format';
import { useMessages } from '../../commun/Messages';
import { deposerDocument, libelleEtatDossier, TAILLE_MAX_OCTETS, TYPES_ACCEPTES } from './listes';
import styles from './metier.module.css';

function auteur(a: EvenementHistorique['auteur']): string {
  return a?.nom ?? (a ? 'agent' : 'système');
}

export function Historique({ chemin, libelles }: { chemin: string; libelles: Record<string, string> }) {
  const h = useQuery({
    queryKey: [chemin],
    queryFn: ({ signal }) => requete<{ elements: EvenementHistorique[] }>(chemin, { signal }).then((r) => r.elements),
    retry: false,
  });
  if (h.isPending) return <Squelette lignes={4} />;
  if (h.error) return <AffichageErreur erreur={h.error} lot="L0-C" fonction="L'historique" />;
  if (h.data.length === 0) return <p>Aucun événement enregistré.</p>;
  return (
    <ol className={styles.chemin} aria-label="Historique">
      {h.data.map((e, i) => (
        <li key={e.id} className={styles.etape} data-courante={i === h.data.length - 1 || undefined} data-type={e.type}>
          <strong>
            {e.type === 'retour' ? 'Retour motivé : ' : e.type === 'reouverture' ? 'Réouverture : ' : e.type === 'migration_version' ? 'Migration de version : ' : ''}
            {e.etat_apres ? (libelles[e.etat_apres] ?? libelleEtatDossier(e.etat_apres)) : (e.type ?? 'Événement')}
          </strong>
          <span>
            {dateHeure(e.horodatage)}, par {auteur(e.auteur)}
            {e.delegation_id ? ', au titre d\'une délégation' : ''}
          </span>
          {e.motif ? <span className={styles.motif}>Motif : {e.motif}</span> : null}
        </li>
      ))}
    </ol>
  );
}

export function Pieces({ objetType, objetId, peutDeposer }: { objetType: string; objetId: string; peutDeposer: boolean }) {
  const client = useQueryClient();
  const { afficher } = useMessages();
  const [fichiers, setFichiers] = useState<File[]>([]);
  const [enCours, setEnCours] = useState(false);
  const cle = ['documents', 'objet', objetType, objetId];
  const docs = useQuery({
    queryKey: cle,
    queryFn: ({ signal }) => requete<{ elements: DocumentResume[] }>('/documents', { signal, parametres: { objet_type: objetType, objet_id: objetId } }).then((r) => r.elements),
    retry: false,
  });
  const deposer = async () => {
    setEnCours(true);
    const echecs: string[] = [];
    for (const f of fichiers) {
      try {
        await deposerDocument(f, objetType, objetId);
      } catch (e) {
        echecs.push(`${f.name} : ${messageErreur(e)}`);
      }
    }
    setEnCours(false);
    await client.invalidateQueries({ queryKey: cle });
    if (echecs.length < fichiers.length) afficher('succes', `${fichiers.length - echecs.length} pièce(s) déposée(s).`);
    if (echecs.length > 0) afficher('erreur', `Dépôt refusé : ${echecs.join(' ; ')}`);
    setFichiers([]);
  };
  return (
    <div className={styles.pile}>
      {docs.isPending ? (
        <Squelette lignes={2} />
      ) : docs.error ? (
        <AffichageErreur erreur={docs.error} lot="L0-C" fonction="La liste des pièces" />
      ) : docs.data.length === 0 ? (
        <EtatVide titre="Aucune pièce">
          <p>Aucun document n&apos;est rattaché.</p>
        </EtatVide>
      ) : (
        <ul className={styles.listePieces}>
          {docs.data.map((d) => (
            <li key={d.id}>
              <Lien href={`/documents?document=${d.id}`}>{d.titre}</Lien>
              <span>version {d.version_courante}</span>
            </li>
          ))}
        </ul>
      )}
      {peutDeposer && !docs.error ? (
        <>
          <FichierChamp libelle="Ajouter des pièces" fichiers={fichiers} onFichiers={setFichiers} accept={TYPES_ACCEPTES} tailleMax={TAILLE_MAX_OCTETS} />
          {fichiers.length > 0 ? (
            <div>
              <Bouton chargement={enCours} onClick={() => void deposer()}>
                Déposer {fichiers.length} fichier{fichiers.length > 1 ? 's' : ''}
              </Bouton>
            </div>
          ) : null}
        </>
      ) : null}
    </div>
  );
}
